import { allowedResolutions, canRefundSelection, paysOut, type ResolutionOption } from '@nvd/claim-shared';
import { useState } from 'react';
import { ClaimedPills } from '../components/ClaimedItems';
import { Icon } from '../components/Icon';
import { Spinner } from '../components/Spinner';
import { StepLayout } from '../components/StepLayout';
import { useAction, usePortal } from '../Portal';
import { buildSubmitRequest, reasonIdsOf, selectionOf } from '../state/machine';

/** One row per outcome: choosing it acts straight away, so the step needs no separate Next button. */
function Choice({
  option,
  hint,
  onClick,
  busy = false,
  disabled = false,
}: {
  option: ResolutionOption;
  hint: string;
  onClick?: () => void;
  busy?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`nvd-claim-portal__choice${disabled && !busy ? ' nvd-claim-portal__choice--unavailable' : ''}`}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy}
    >
      <span className="nvd-claim-portal__choice-icon">
        <Icon name={option.icon} />
      </span>
      <span className="nvd-claim-portal__choice-text">
        <span className="nvd-claim-portal__choice-label">{option.label}</span>
        <span className="nvd-claim-portal__choice-hint">{hint}</span>
      </span>
      <span className="nvd-claim-portal__choice-go">{busy ? <Spinner small /> : <Icon name="chevron" />}</span>
    </button>
  );
}

export function Resolution() {
  const { config, t, api, state, dispatch } = usePortal();
  const { busy, error, run } = useAction();
  const [pending, setPending] = useState<string | null>(null);
  const view = state.view;
  if (!view) return null;

  const selection = selectionOf(state.draft);
  const refundAvailable = canRefundSelection(view, selection);
  const options = allowedResolutions(config, reasonIdsOf(state.draft));

  const choose = (option: ResolutionOption) => {
    setPending(option.id);
    void run(async () => {
      const preview = await api.preview({ items: selection, resolutionId: option.id });
      dispatch({ type: 'PREVIEWED', resolution: option, preview });
      // A reorder always gets another screen, refund and store credit only when automated; the rest is submitted here.
      if (preview.kind === 'manual' && option.kind !== 'reorder') {
        const { outcome } = await api.submit(buildSubmitRequest(state.draft, option.id));
        dispatch({ type: 'SUBMITTED', outcome });
      }
    }).finally(() => setPending(null));
  };

  return (
    <StepLayout title={t('resolution.title')} error={error} busy={busy} onBack={() => dispatch({ type: 'BACK' })}>
      <ClaimedPills />
      <div className="nvd-claim-portal__choices">
        {options.map((option) => {
          const unavailable = paysOut(option) && !refundAvailable;
          if (unavailable && !option.showWhenUnavailable) return null;
          return (
            <Choice
              key={option.id}
              option={option}
              hint={unavailable ? t('resolution.unavailable') : option.description}
              onClick={unavailable ? undefined : () => choose(option)}
              busy={pending === option.id}
              disabled={unavailable || busy}
            />
          );
        })}
      </div>
    </StepLayout>
  );
}
